import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Loader from '../../../../components/ui/Loader';
import DataGrid from '../../../../components/DataGrid';
import GridButton from '../../../../components/ui/GridButton';
import Icon from '../../../../components/ui/Icon';
import ActionBar from '../../../../components/ui/ActionBar';
import AttendanceLogDetailsModal from './AttendanceLogDetailsModal';
import RetiredMemberDetailsModal from './RetiredMemberDetailsModal';
import {
    getAgMemberMeetingDetails,
    getMemberComments,
    getRetiredMemberDocumentsByMemberId,
    getRetiredMembersByAgId,
} from '../../../../ducks/advisoryGroup';
import { AppLoadContext } from '../../../../components/ui/AppLoadContext';
import {
    calculateAttendancePercentage,
    pickProps,
    toDate,
} from '../../../../utils/utils';
import DocumentDetailsModal from './DocumentDetailsModal';

const RetiredMembers = () => {
    const { agId } = useParams();
    const dispatch = useDispatch();
    const isAppLoaded = useContext(AppLoadContext);

    const retiredMemberData = useSelector((state) => state.advisoryGroup.retiredMemberDetails);
    const currentUser = useSelector((state) => state.user.currentUser);

    const [openRetiredMemberDetails, setOpenRetiredMemberDetails] = useState(false);
    const [openAttendanceLogDetails, setOpenAttendanceLogDetails] = useState(false);
    const [openDocumentsDetails, setOpenDocumentsDetails] = useState(false);
    const [currentRowData, setCurrentRowData] = useState(null);
    const [retiredData, setRetiredData] = useState(null);

     const defaultSort = [
        {
            id: 'retireDate',
            desc: true,
        },
    ];

    useEffect(() => {
        if (isAppLoaded) {
            if (!currentUser) {
                return;
            }
            dispatch(getRetiredMembersByAgId(agId));
        }
    }, [dispatch, isAppLoaded, currentUser]);

    const columns = [
        {
            id: 'firstName',
            header: 'First Name',
            accessorKey: 'firstName',
            sortingFn: 'text',
        },
        {
            id: 'lastName',
            header: 'Last Name',
            accessorKey: 'lastName',
            sortingFn: 'text',
        },
        {
            id: 'city',
            header: 'City',
            accessorKey: 'city',
            sortingFn: 'text',
        },
        {
            id: 'state',
            header: 'State',
            accessorKey: 'state',
            sortingFn: 'text',
        },
        {
            id: 'postalCode',
            header: 'Zip',
            accessorKey: 'postalCode',
            sortingFn: 'text',
        },
        {
            id: 'startDate',
            header: 'Onboarding Date',
            accessorKey: 'startDate',
            sortType: 'date',
            accessorFn: (row) => (new Date(row.startDate)),
            cell: ({ row }) => `${toDate(row.original.startDate)}`,
            sortingFn: 'datetime'
        },
        {
            id: 'retireDate',
            header: 'Retirement Date',
            accessorKey: 'retireDate',
            sortType: 'date',
            accessorFn: (row) => (new Date(row.retireDate)),
            cell: ({ row }) => `${toDate(row.original.retireDate)}`,
            sortingFn: 'datetime'
        },
        {
            id: 'retirementReason',
            header: 'Retirement Reason',
            accessorKey: 'retirementReason',
            accessorFn: (row) => row?.retirementReason ? row?.retirementReason : null,
            sortingFn: 'text',
            filterFn: 'equalsString',
        },
        {
            id: 'attendanceRecord',
            header: 'Attendance Record',
            accessorKey: 'attendanceRecord',
            accessorFn: (row) => row?.attendedAttendance !== null && row?.totalAttendance !== null ? `${calculateAttendancePercentage(row.attendedAttendance, row.totalAttendance)}%` : null,
            sortingFn: 'alphanumeric',
        },
        {
            id: 'meetingDate',
            header: 'Last Meeting Attended',
            accessorKey: 'meetingDate',
            sortType: 'date',
            accessorFn: (row) => (new Date(row.meetingDate)),
            cell: ({ row }) => `${toDate(row.original.meetingDate)}`,
            sortingFn: 'datetime'
        },
        {
            id: 'actions',
            header: 'Actions',
            size: 90,
            cell: ({ row }) => (
                <>
                    <GridButton
                        variant="light"
                        size="sm"
                        className="me-1 my-1"
                        title="View"
                        onClick={() => handleRetiredDetailsView(row.original)}>
                        <Icon
                            icon="view"
                            size={14}
                        />
                    </GridButton>
                    <GridButton
                        variant="light"
                        size="sm"
                        className="my-1"
                        title="Attendance Log"
                        onClick={() => handleAttendanceLog(row.original)}>
                        <Icon
                            icon="attendance-log"
                            size={14}
                        />
                    </GridButton>
                    <GridButton
                        variant="light"
                        size="sm"
                        className="my-1"
                        title="Documents"
                        onClick={() => handleDocuments(row.original)}>
                        <Icon
                            icon="documents"
                            size={14}
                        />
                    </GridButton>
                </>
            ),
        },
    ];

    const handleRetiredDetailsView = (data) => {
        const memberData = pickProps(data, [
            'firstName',
            'lastName',
            'phone',
            'startDate',
            'healthPlanMemberId',
            'communicationLastDate',
            'addressLine1',
            'email',
        ]);
        setRetiredData(memberData);
        setOpenRetiredMemberDetails(true);
        dispatch(getMemberComments(data.id));
        console.log(data);
    };

    const handleAttendanceLog = (data) => {
        setOpenAttendanceLogDetails(true);
        dispatch(getAgMemberMeetingDetails(data.id));
        setCurrentRowData(data);
    };

    const handleDocuments = (data) => {
        setOpenDocumentsDetails(true);
        dispatch(getRetiredMemberDocumentsByMemberId(data.id));
    };

    const handleAttendanceLogModalClose = () => {
        setOpenAttendanceLogDetails(false);
        setRetiredData(null);
    };

    const handleDocumentsModalClose = () => {
        setOpenDocumentsDetails(false);
        setRetiredData(null);
    };

    const handleRetiredDetailsModalClose = () => {
        setOpenRetiredMemberDetails(false);
        setRetiredData(null);
    };

    return (
        <>
            <ActionBar title="Retired Members"></ActionBar>
            <div>
                {retiredMemberData === null ? (
                    <Loader />
                ) : (
                    <DataGrid
                        data={
                            retiredMemberData?.length > 0
                                ? retiredMemberData
                                : []
                        }
                        columns={columns}
                        sort={defaultSort}
                        enableFilters={false}
                    />
                )}
            </div>

            {openRetiredMemberDetails && (
                <RetiredMemberDetailsModal
                    show={openRetiredMemberDetails}
                    data={retiredData}
                    onCancel={handleRetiredDetailsModalClose}
                    viewOnly={true}
                />
            )}

            {openAttendanceLogDetails && (
                <AttendanceLogDetailsModal
                    show={openAttendanceLogDetails}
                    onCancel={handleAttendanceLogModalClose}
                    data={currentRowData}
                    viewOnly={true}
                />
            )}

            {openDocumentsDetails && (
                <DocumentDetailsModal
                    show={openDocumentsDetails}
                    onCancel={handleDocumentsModalClose}
                />
            )}
        </>
    );
};

export default RetiredMembers;
